import { isValid, parseISO } from 'date-fns';

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * Calendar day for a one-off report timestamp.
 * A migrated date is midnight UTC. Formatting that instant in local time
 * shows the previous day west of UTC. A date-only string is already a
 * calendar day, so it is left on that day.
 */
export const formatStoredReportDate = (value?: string | null): string => {
  const trimmed = value?.trim() ?? '';
  if (!trimmed) return '';
  const dateOnly = trimmed.match(DATE_ONLY);
  if (dateOnly) return `${dateOnly[2]}-${dateOnly[3]}-${dateOnly[1]}`;
  const parsed = parseISO(trimmed);
  if (!isValid(parsed)) return trimmed;
  const month = String(parsed.getUTCMonth() + 1).padStart(2, '0');
  const day = String(parsed.getUTCDate()).padStart(2, '0');
  return `${month}-${day}-${parsed.getUTCFullYear()}`;
};

const browserZoneName = (parsed: Date): string =>
  new Intl.DateTimeFormat('en-US', { timeZoneName: 'short' })
    .formatToParts(parsed)
    .find((part) => part.type === 'timeZoneName')?.value ?? '';

/** Requested at, on the browser's clock. */
export const formatBrowserDateTime = (value?: string | null): string => {
  const trimmed = value?.trim() ?? '';
  if (!trimmed) return '';
  const parsed = parseISO(trimmed);
  if (!isValid(parsed)) return trimmed;
  const month = String(parsed.getMonth() + 1).padStart(2, '0');
  const day = String(parsed.getDate()).padStart(2, '0');
  const year = parsed.getFullYear();
  const minutes = String(parsed.getMinutes()).padStart(2, '0');
  const hours24 = parsed.getHours();
  const suffix = hours24 >= 12 ? 'PM' : 'AM';
  const hours12 = hours24 % 12 || 12;
  const zone = browserZoneName(parsed);
  const clock = `${month}-${day}-${year} ${hours12}:${minutes} ${suffix}`;
  return zone ? `${clock} ${zone}` : clock;
};
